/**
 * Create remediation documents (arbitium_remediations).
 * Blueprint: arbitium_remediations.json.
 */

import { postRing } from '../core';

const RING_REMEDIATIONS = 'arbitium_remediations';

export interface CreateRemediationOptions {
  /** Reference to threat event document _id in arbitium_threat_events */
  threat_event_id?: string;
  status?: 'pending' | 'confirmed' | 'cancelled' | 'in_progress' | 'completed' | 'reverted';
  execution_mode?: 'auto' | 'gated';
  operations?: unknown[];
  total_tokens?: number;
  formed_at?: string;
  assigned_at?: string;
  decided_at?: string;
  verified_at?: string;
  reverted_at?: string;
  revert_cause?: 'validation_failed' | 'human_rollback';
  test_id?: string;
  origin?: string;
}

export interface RemediationItem {
  _id: string;
  title: string;
  threat_event_id?: string;
  status?: string;
  execution_mode?: string;
  operations?: unknown[];
  total_tokens?: number;
  formed_at?: string;
  assigned_at?: string;
  /** When it was handed to a higher level of management; the Threat Feed reads it as the Escalated state. */
  escalated_at?: string;
  decided_at?: string;
  verified_at?: string;
  reverted_at?: string;
  revert_cause?: string;
  test_id?: string;
  origin?: string;
  [key: string]: unknown;
}

/**
 * Creates an arbitium_remediation document. Title is required; threat_event_id and other fields optional.
 */
export async function createRemediationDoc(
  title: string,
  options?: CreateRemediationOptions
): Promise<RemediationItem | null> {
  const payload: Record<string, unknown> = { title };
  if (options?.threat_event_id) payload.threat_event_id = options.threat_event_id;
  if (options?.status) payload.status = options.status;
  if (options?.execution_mode) payload.execution_mode = options.execution_mode;
  if (options?.operations) payload.operations = options.operations;
  if (options?.total_tokens != null) payload.total_tokens = options.total_tokens;
  if (options?.formed_at) payload.formed_at = options.formed_at;
  if (options?.assigned_at) payload.assigned_at = options.assigned_at;
  if (options?.decided_at) payload.decided_at = options.decided_at;
  if (options?.verified_at) payload.verified_at = options.verified_at;
  if (options?.reverted_at) payload.reverted_at = options.reverted_at;
  if (options?.revert_cause) payload.revert_cause = options.revert_cause;
  if (options?.test_id) payload.test_id = options.test_id;
  if (options?.origin) payload.origin = options.origin;

  const res = await postRing<RemediationItem>(RING_REMEDIATIONS, payload);
  return res.success && res.item ? res.item : null;
}
